import { html } from '../application_element/element.js';
import BaseMenu from '../base_menu/element.js';
import translations from './translations.js';
import copyNodeToClipboard from '@effectivastudio/copy_node_to_clipboard';
import { chatConfig } from '../chat/lib/config.js';
import '../icon_block/element.js';
import '../dot_menu/element.js';

import { ChatMessageEvent } from '../chat_message_base/lib/chat_message_event.js';

/**
 * The dot-dot-dot menu for a message component
 *
 * @prop message - Chat message
 * @prop messageId - The id of a chat message
 * @prop editOn - Should display edit action
 * @prop deleteOn - Should display delete action
 * @prop restoreOn - Should display restore action
 * @prop replyOn - Should display reply action
 * @prop visitProfileOn - Should display visit profile action
 * @prop infoOn - Should display info action
 */
export default class ChatMessageMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  static properties = {
    message: { type: Object, attribute: false },
    messageId: { type: String },
    infoOn: { type: Boolean },
    copyOn: { type: Boolean },
    visitProfileOn: { type: Boolean },
    editOn: { type: Boolean },
    deleteOn: { type: Boolean },
    restoreOn: { type: Boolean },
    replyOn: { type: Boolean },
  };

  /** @internal */
  get instanceId() {
    return this.messageId;
  }

  /** @internal */
  get infoUrl() {
    return `/messages/${this.messageId}/user_trackings`;
  }

  /** @internal */
  get infoItem() {
    return {
      title: this.t('actions.info'),
      action: this.actionFor('messageInfo', [this.infoUrl]),
      icon: chatConfig.icons.menu.info,
      cssClass: 'info',
    };
  }

  /** @internal */
  get copyItem() {
    return {
      title: this.t('actions.copy'),
      action: this.actionFor('messageCopy'),
      icon: chatConfig.icons.menu.copy,
      cssClass: 'copy',
    };
  }

  /** @internal */
  get visitProfileItem() {
    return {
      title: this.t('actions.visit_profile'),
      action: this.actionFor('visitProfile'),
      icon: chatConfig.icons.menu.visit_profile,
      cssClass: 'visit-profile',
    };
  }

  /** @internal */
  get editItem() {
    return {
      title: this.t('actions.edit'),
      action: this.actionFor('messageTrigger', ['edit']),
      icon: chatConfig.icons.menu.edit,
      cssClass: 'edit',
    };
  }

  /** @internal */
  get restoreItem() {
    return {
      title: this.t('actions.restore'),
      action: this.actionFor('messageTrigger', ['restore']),
      icon: chatConfig.icons.menu.restore,
      cssClass: 'restore',
    };
  }

  /** @internal */
  get replyItem() {
    return {
      title: this.t('actions.reply'),
      action: this.actionFor('messageTrigger', ['reply']),
      icon: chatConfig.icons.menu.reply,
      cssClass: 'reply',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get deleteItem() {
    return {
      title: this.t('actions.delete'),
      action: this.actionFor('messageTrigger', ['delete']),
      icon: chatConfig.icons.menu.delete,
      role: 'destructive',
      cssClass: 'delete text-danger',
    };
  }

  /** @internal */
  get items() {
    return [
      this.infoOn && this.infoItem,
      this.copyOn && this.copyItem,
      this.visitProfileOn && this.visitProfileItem,
      this.restoreOn && this.restoreItem,
      this.replyOn && this.replyItem,
      this.editOn && this.editItem,
      this.deleteOn &&
        (this.copyOn || this.restoreOn || this.editOn) &&
        this.divider,
      this.deleteOn && this.deleteItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'ef-chat-message-menu',
      id: this.instanceId,
      method: method,
      args: args,
    };
  }

  messageTrigger(name) {
    this.message.dispatchEvent(new ChatMessageEvent(name, this.message));
  }

  messageCopy() {
    copyNodeToClipboard(this.getRootNode().querySelector('ef-truncate'));
    this.commonActions.displaySnackBarSuccess(this.t('snack.copied_success'));
  }

  async messageInfo(url) {
    try {
      await this.commonActions.get(url);
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  visitProfile() {
    this.commonActions.visit(this.message.userUrl);
  }

  render() {
    return html`
      <ef-dot-menu
        .config=${this.dotMenuConfig}
        .boundaryElement=${this.message.chatList}>
      </ef-dot-menu>
    `;
  }
}

ChatMessageMenu.registerTagName('ef-chat-message-menu');
ChatMessageMenu.registerCommonActions('ef-chat-message-menu');

customElements.get('ef-chat-message-menu') ||
  customElements.define('ef-chat-message-menu', ChatMessageMenu);
